import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Validator from '../../../common/forms/Validator';
import ActionsGroup from '../../../common/ui/ItemActions';
import { labels } from '../../../../constants/text';
import { formatPrice } from '../../../../helpers/formatData';
import { isJobSaved } from '../../../../helpers/jobs';
import { getCentPrice, tryParseMonetaryValue } from '../../../../helpers/general';
import ErrorMessage from '../../../common/forms/ErrorMessage';
import Loader from '../../../common/ui/Loader';

export default class Skills extends Validator {
  static propTypes = {
    jobDetails: PropTypes.shape({
      job_skill_id: PropTypes.number.isRequired,
      skill_label: PropTypes.string.isRequired,
      worker_pay_rate: PropTypes.number.isRequired,
      job_cost_rate: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired
    }),
    jobRates: PropTypes.shape({
      worker_pay_rate: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ]),
      job_cost_rate: PropTypes.oneOfType([
        PropTypes.number.isRequired,
        PropTypes.string.isRequired // in case when field is empty it will be equal to empty string
      ])
    }),
    showEditIcons: PropTypes.bool.isRequired,
    skillsList: PropTypes.array.isRequired,
    getAllSkills: PropTypes.func.isRequired,
    editJob: PropTypes.func.isRequired,
    getCostPayRateByData: PropTypes.func.isRequired,
    updateJobRates: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    const { job_skill_id, skill_label, description } = props.jobDetails;
    this.initialSkillData = {
      job_skill_id,
      skill_label,
      description
    };
    this.state = {
      isLoading: false,
      isEditing: false,
      ...this.initialSkillData
    };
  }

  validationRules = {
    worker_pay_rate: { required: true },
    job_cost_rate: { required: true }
  };

  componentDidMount () {
    this.props.getAllSkills();
  }

  startLoading = () => {
    this.setState({ isLoading: true });
  };

  stopLoading = () => {
    this.setState({ isLoading: false });
  };

  startEditing = () => {
    this.setState({ isEditing: true });
  };

  stopEditing = () => {
    this.setState({ isEditing: false });
  };

  handleChangeJobSkill = event => {
    const job_skill_id = parseInt(event.target.value, 10);
    const selectedSkill = this.props.skillsList.find(skillData => (
      skillData.job_skill_id === job_skill_id
    ));
    this.setState({
      job_skill_id,
      skill_label: selectedSkill.skill_label,
      description: selectedSkill.job_comments_template
    });
    this.props.getCostPayRateByData({ job_skill_id });
  };

  handleChangeCostPayRate = event => {
    const { value, name } = event.target;
    const resolvedValue = tryParseMonetaryValue(value);
    if (!_.isNull(resolvedValue)) {
      this.props.updateJobRates({ [name]: resolvedValue });
    }
  };

  cancelEditing = () => {
    this.setState({
      ...this.initialSkillData
    });
    const { jobDetails, updateJobRates } = this.props;
    const { job_cost_rate, worker_pay_rate } = jobDetails;
    updateJobRates({ job_cost_rate, worker_pay_rate });
    this._resetErrors();
    this.stopEditing();
  };

  submitEditing = e => {
    e.preventDefault();
    const { job_cost_rate, worker_pay_rate } = this.props.jobRates;
    if (!this.validate(this.validationRules, { ...this.state, job_cost_rate, worker_pay_rate })) {
      return;
    }
    this.startLoading();
    const { job_skill_id, description } = this.state;
    this.props.editJob({
      job_skill_id,
      job_cost_rate: getCentPrice(job_cost_rate),
      worker_pay_rate: getCentPrice(worker_pay_rate),
      description
    }).then(() => {
      this.stopEditing();
      this.stopLoading();
    });
  };

  render () {
    const { isLoading, isEditing, job_skill_id, skill_label, description } = this.state;
    const { job_cost_rate, worker_pay_rate } = this.props.jobRates;
    const { showEditIcons, jobDetails, skillsList } = this.props;
    const jobStatusIsSaved = isJobSaved(jobDetails.status);
    const selectedSkillData = skillsList.find(skill => skill.job_skill_id === job_skill_id);
    let skillImgUrl = '';
    if (selectedSkillData) {
      skillImgUrl = selectedSkillData.icon_url;
    }
    const errors = this.validationErrors;
    return (
      <section className='skill-info'>
        <form onSubmit={this.submitEditing}>
          <div className='d-flex justify-content-between mb-2'>
            <h4 className='job-details-section-header'>Skills</h4>
            { showEditIcons && !isLoading && (
              <ActionsGroup
                isEditing={isEditing}
                enterEditMode={this.startEditing}
                exitEditMode={this.cancelEditing}
              />
            )}
          </div>
          { isLoading ? <div className='loader-wrapper'><Loader /></div> : (
            <Fragment>
              <div className='d-flex align-items-center'>
                <div
                  className='skill-img'
                  style={{ backgroundImage: `url('${skillImgUrl}')` }}
                />
                { isEditing && jobStatusIsSaved ? (
                  <div>
                    <select
                      className='styled-select'
                      value={job_skill_id}
                      name='skill'
                      onChange={this.handleChangeJobSkill}
                    >
                      {skillsList.map(skill =>
                        <option value={skill.job_skill_id} key={skill.id}>
                          {skill.skill_label}
                        </option>
                      )}
                    </select>
                  </div>
                ) : (
                  <p>{`${skill_label || ''}`}</p>
                )}
              </div>
              <div>
                { isEditing && jobStatusIsSaved && (
                  <p className='mt-2'>{ description }</p>
                )}
                <div className={`mt-3 ${isEditing ? 'd-flex' : ''}`}>{ labels.pay_rate } {` `}
                  { isEditing ? (
                    <Fragment>
                      $
                      <div>
                        <input
                          className='price-input'
                          type='number'
                          step='any'
                          min='0'
                          placeholder='0'
                          name='worker_pay_rate'
                          value={worker_pay_rate}
                          onChange={this.handleChangeCostPayRate}
                        />
                        <ErrorMessage errorText={errors.worker_pay_rate} />
                      </div>
                    </Fragment>
                  ) : formatPrice(worker_pay_rate)} /hr
                </div>
                <div className={`mt-3 ${isEditing ? 'd-flex' : ''}`}>{ labels.cost_rate } {` `}
                  { isEditing ? (
                    <Fragment>
                      $
                      <div>
                        <input
                          className='price-input'
                          type='number'
                          step='any'
                          min='0'
                          placeholder='0'
                          name='job_cost_rate'
                          value={job_cost_rate}
                          onChange={this.handleChangeCostPayRate}
                        />
                        <ErrorMessage errorText={errors.job_cost_rate} />
                      </div>
                    </Fragment>
                  ) : formatPrice(job_cost_rate)} /hr
                </div>
              </div>
            </Fragment>
          )}
        </form>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/jobDetails/JobDetailsTab/Skills.js